import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import AppLayout from '@cloudscape-design/components/app-layout';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import Box from '@cloudscape-design/components/box';

const BASE_NAV_ITEMS: SideNavigationProps.Item[] = [
  {
    type: 'section-group',
    title: 'Incidents',
    items: [
      { type: 'link', text: 'Incident Analysis', href: '/' },
      { type: 'link', text: 'Search Incidents', href: '/incidents' },
    ],
  },
  {
    type: 'section-group',
    title: 'Off-field landing',
    items: [
      { type: 'link', text: 'Off-Field Landing Planner', href: '/landing-planner' },
    ],
  },
  {
    type: 'section-group',
    title: 'Phraseology',
    items: [
      { type: 'link', text: 'Phraseology Assessments', href: '/phraseology' },
    ],
  },
  {
    type: 'section-group',
    title: 'Simulator',
    items: [
      { type: 'link', text: 'Sim Control (X-Plane)', href: '/sim-control' },
      { type: 'link', text: 'Sim Sessions', href: '/sim-control/sessions' },
    ],
  },
  { type: 'divider' },
  {
    type: 'section',
    text: 'Coming Soon',
    items: [
      // 'Simulation' and 'Physical Sim' were placeholders from before the
      // X-Plane plugin shipped — those use cases are now covered by
      // Sim Control above and don't belong in Coming Soon.
      { type: 'link', text: 'Instructor Marketplace', href: '/instructor-marketplace' },
    ],
  },
];

// Admin-only group. The Aircraft Catalog is a reference/authoring surface, so
// it lives here rather than in the main nav.
const ADMIN_SECTION: SideNavigationProps.Item = {
  type: 'section-group',
  title: 'Admin',
  items: [
    { type: 'link', text: 'Admin Dashboard', href: '/admin' },
    { type: 'link', text: 'Aircraft Catalog', href: '/aircraft' },
  ],
};

/**
 * App shell layout using Cloudscape AppLayout with TopNavigation and SideNavigation.
 * Shows admin nav item only for users in the Admin_User Cognito group.
 */
const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    async function loadSession() {
      try {
        // forceRefresh: false, skip Identity Pool credential fetch
        const session = await fetchAuthSession({ forceRefresh: false });
        const idToken = session.tokens?.idToken;
        if (idToken) {
          const payload = idToken.payload;
          const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
          setIsAdmin(groups.includes('Admin_User'));
          const email = (payload['email'] as string | undefined) ?? '';
          setUserEmail(email);
        }
      } catch {
        // Session fetch failed — non-critical, defaults are fine
      }
    }
    loadSession();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/login');
    }
  };

  const navItems: SideNavigationProps.Item[] = isAdmin
    ? [...BASE_NAV_ITEMS, { type: 'divider' }, ADMIN_SECTION]
    : BASE_NAV_ITEMS;

  const handleNavFollow = (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    event.preventDefault();
    if (event.detail.href) {
      navigate(event.detail.href);
    }
  };

  return (
    <>
      <div id="top-nav">
        <TopNavigation
          identity={{
            href: '/',
            title: 'ReFly',
            logo: {
              src: '/logo.svg',
              alt: 'ReFly Aviation Safety Platform',
            },
            onFollow: (e) => {
              e.preventDefault();
              navigate('/');
            },
          }}
          utilities={[
            {
              type: 'menu-dropdown',
              text: userEmail || 'Account',
              iconName: 'user-profile',
              items: [
                { id: 'account', text: 'Account Settings' },
                { id: 'signout', text: 'Sign Out' },
              ],
              onItemClick: ({ detail }) => {
                if (detail.id === 'signout') {
                  handleSignOut();
                } else if (detail.id === 'account') {
                  navigate('/account');
                }
              },
            },
          ]}
        />
      </div>
      <AppLayout
        navigation={
          <SideNavigation
            header={{ text: 'ReFly', href: '/' }}
            activeHref={location.pathname}
            items={navItems}
            onFollow={handleNavFollow}
          />
        }
        content={
          <>
            <Outlet />
            <Box
              textAlign="center"
              padding={{ vertical: 'xl', horizontal: 'l' }}
              color="text-body-secondary"
              variant="small"
            >
              © 2026{' '}
              <a
                href="https://halyardanalytics.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Halyard Analytics LLC
              </a>
            </Box>
          </>
        }
        toolsHide
        headerSelector="#top-nav"
      />
    </>
  );
};

export default AppShell;
